/**
 * Hook: GET /api/t/{tenantSlug}/ratings/by-resource?resource_key=<key>
 *
 * Returns the current user's rating + aggregate up/down counts for a resource.
 * Cache key: ["ai-rating", tenantSlug, resourceKey].
 * staleTime: Infinity — ratings do not change out-of-band; mutations patch cache
 * directly via `queryClient.setQueryData`.
 *
 * Design reference: backend/docs/batch_b12_design.md §2 (hydration).
 */
import { useQuery, useMutation, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";

interface MyRatingDetail {
  rating: "up" | "down";
  comment: string | null;
  created_at: string;
  updated_at: string;
}

interface RatingCounts {
  up: number;
  down: number;
}

interface RatingByResourceData {
  my_rating: MyRatingDetail | null;
  counts: RatingCounts;
}

interface RatingSubmitResponse {
  id: string;
  action_type: string;
  resource_key: string;
  rating: string;
  comment: string | null;
  model_used: string;
  created_at: string;
  updated_at: string;
  was_inserted: boolean;
}

export type ActionType = "ai_evaluation" | "interview_prep" | "interview_analysis" | "rank_one";

function ratingCacheKey(tenantSlug: string | undefined, resourceKey: string) {
  return ["ai-rating", tenantSlug, resourceKey] as const;
}

export function useRatingByResource(
  resourceKey: string,
): UseQueryResult<RatingByResourceData, ApiError> {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return useQuery<RatingByResourceData, ApiError>({
    queryKey: ratingCacheKey(tenantSlug, resourceKey),
    queryFn: async () => {
      const r = await apiClient.get<RatingByResourceData>(
        `/api/t/${tenantSlug}/ratings/by-resource`,
        { params: { resource_key: resourceKey } },
      );
      return r.data;
    },
    enabled: !!tenantSlug && !!resourceKey,
    staleTime: Infinity,
  });
}

interface SubmitRatingVars {
  actionType: ActionType;
  resourceKey: string;
  rating: "up" | "down";
  comment?: string | null;
}

/** POST /ratings — submit or flip a rating. Returns the full response. */
export function useSubmitRating() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();

  return useMutation<RatingSubmitResponse, ApiError, SubmitRatingVars>({
    mutationFn: async ({ actionType, resourceKey, rating, comment }) => {
      const r = await apiClient.post<RatingSubmitResponse>(
        `/api/t/${tenantSlug}/ratings`,
        {
          action_type: actionType,
          resource_key: resourceKey,
          rating,
          ...(comment != null ? { comment } : {}),
        },
      );
      return r.data;
    },
    onSuccess: (data, vars) => {
      // Patch cache so re-fetch is not needed.
      queryClient.setQueryData<RatingByResourceData>(
        ratingCacheKey(tenantSlug, vars.resourceKey),
        (old) => {
          const newMyRating: MyRatingDetail = {
            rating: data.rating as "up" | "down",
            comment: data.comment,
            created_at: data.created_at,
            updated_at: data.updated_at,
          };
          const wasDown = old?.my_rating?.rating === "down";
          const wasUp = old?.my_rating?.rating === "up";
          const nowUp = data.rating === "up";
          const nowDown = data.rating === "down";
          const prevCounts = old?.counts ?? { up: 0, down: 0 };
          // Adjust counts: subtract old direction, add new direction.
          const counts: RatingCounts = {
            up: prevCounts.up + (nowUp ? 1 : 0) - (wasUp ? 1 : 0),
            down: prevCounts.down + (nowDown ? 1 : 0) - (wasDown ? 1 : 0),
          };
          return { my_rating: newMyRating, counts };
        },
      );
    },
  });
}

interface ClearRatingVars {
  actionType: ActionType;
  resourceKey: string;
}

/** DELETE /ratings — clear own rating. */
export function useClearRating() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, ClearRatingVars>({
    mutationFn: async ({ actionType, resourceKey }) => {
      await apiClient.delete(`/api/t/${tenantSlug}/ratings`, {
        data: { action_type: actionType, resource_key: resourceKey },
      });
    },
    onSuccess: (_data, vars) => {
      queryClient.setQueryData<RatingByResourceData>(
        ratingCacheKey(tenantSlug, vars.resourceKey),
        (old) => {
          const prevCounts = old?.counts ?? { up: 0, down: 0 };
          const wasUp = old?.my_rating?.rating === "up";
          const wasDown = old?.my_rating?.rating === "down";
          return {
            my_rating: null,
            counts: {
              up: Math.max(0, prevCounts.up - (wasUp ? 1 : 0)),
              down: Math.max(0, prevCounts.down - (wasDown ? 1 : 0)),
            },
          };
        },
      );
    },
  });
}
